import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import ContainerSelector from './container-selector';
import { Container, SubmitJobRequest } from './types';
import { submitComputeJob } from '../../middleware/api/compute_job_apis';
import ToolButton, { ToolButtonType } from '../../tools/tool-button';
import { useAppSelector } from '../../app/hooks';
import { useTranslation } from 'react-i18next';
import '../../App.css';

interface JobSubmissionFormProps {
  /** Whether the modal is visible */
  show: boolean;

  /** Callback when modal is closed */
  onClose: () => void;

  /** Callback when job is successfully submitted */
  onSuccess: (jobId: string) => void;
}

const JobSubmissionForm: React.FC<JobSubmissionFormProps> = ({
  show,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const sG_DarkMode: boolean = useAppSelector((state) => state.global.darkMode);

  // Form state
  const [jobName, setJobName] = useState<string>('');
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);
  const [script, setScript] = useState<string>('');
  const [parameters, setParameters] = useState<string>('');

  // UI state
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    jobName?: string;
    container?: string;
    script?: string;
  }>({});

  // Reset form when modal is opened/closed
  useEffect(() => {
    if (!show) {
      // Reset form when modal closes
      setJobName('');
      setSelectedContainer(null);
      setScript('');
      setParameters('');
      setError(null);
      setValidationErrors({});
      setSubmitting(false);
    }
  }, [show]);

  // Update script when container is selected
  useEffect(() => {
    if (selectedContainer) {
      setScript(selectedContainer.default_script);
      setValidationErrors((prev) => ({ ...prev, container: undefined }));
    }
  }, [selectedContainer]);

  const validateForm = (): boolean => {
    const errors: { jobName?: string; container?: string; script?: string } = {};

    if (!jobName.trim()) {
      errors.jobName = 'Job name is required';
    }

    if (!selectedContainer) {
      errors.container = 'Please select a container image';
    }

    if (!script.trim()) {
      errors.script = 'Script is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!selectedContainer) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload: SubmitJobRequest = {
        job_name: jobName.trim(),
        job_type: 'analysis',
        submission_params: {
          container_image: selectedContainer.container_image,
          script: script.trim(),
          dataset: 'https://webdav.data.skao.int/3811823/dataproduct.fits',
          parameters: parameters.trim() || undefined,
          system_parameters: selectedContainer.system_parameters || {
            queue_name: 'spain_harvester',
          },
        },
        polling_duration_days: 7,
        idempotency_key: `job-${Date.now()}-${Math.random().toString(36).substring(7)}`,
      };

      const response = await submitComputeJob(payload);

      if (response.job_id) {
        onSuccess(response.job_id);
        onClose();
      } else {
        throw new Error('Job submitted but no job ID returned');
      }
    } catch (err) {
      console.error('Failed to submit job:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to submit job. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!submitting) {
      onClose();
    }
  };

  const modalStyle = {
    color: sG_DarkMode ? '#fff' : '#000',
    borderRadius: '0px',
    margin: '0px',
    border: 'none',
  };

  return (
    <Modal show={show} onHide={handleCancel} size="lg" backdrop="static" centered>
      <div
        className="solid-panel-background"
        style={modalStyle}
        data-theme={sG_DarkMode ? 'dark' : 'light'}
      >
        <Modal.Header closeButton={!submitting}>
          <Modal.Title style={{ fontSize: '1em', fontWeight: '400' }}>
            {t('Submit Compute Job')}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ borderRadius: '0px' }}>
          <Form onSubmit={handleSubmit}>
            {error && (
              <div style={{ color: 'red', margin: '5px 0px 10px 0px' }}>{error}</div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                {t('Job Name')} <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              <input
                className="input-field"
                type="text"
                value={jobName}
                onChange={(e) => {
                  setJobName(e.target.value);
                  setValidationErrors((prev) => ({ ...prev, jobName: undefined }));
                }}
                data-disabled={submitting ? 'T' : 'F'}
                placeholder={t('Please enter a descriptive name for your compute job')}
                data-error={!!validationErrors.jobName ? 'T' : 'F'}
                autoComplete="off"
              />
              {validationErrors.jobName && (
                <div style={{ color: 'red', marginTop: '0.25rem' }}>
                  {validationErrors.jobName}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {t('Container Image')} <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              <ContainerSelector
                value={selectedContainer}
                onChange={setSelectedContainer}
                disabled={submitting}
                placeholder={t('Select a container image')}
              />
              {validationErrors.container && (
                <div style={{ color: 'red', marginTop: '0.5rem', fontSize: '1rem' }}>
                  {validationErrors.container}
                </div>
              )}
              {selectedContainer && (
                <Form.Text
                  className="text-colour"
                  style={{ marginTop: '0.5rem', fontSize: '1rem' }}
                  data-muted="T"
                >
                  {selectedContainer.description}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {t('Script')} <span style={{ color: 'red' }}>*</span>
              </Form.Label>
              <input
                className="input-field"
                type="text"
                value={script}
                onChange={(e) => {
                  setScript(e.target.value);
                  setValidationErrors((prev) => ({ ...prev, script: undefined }));
                }}
                data-disabled={submitting ? 'T' : 'F'}
                placeholder="/usr/local/bin/dummy"
                data-error={!!validationErrors.script ? 'T' : 'F'}
                autoComplete="off"
              />
              {validationErrors.script && (
                <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {validationErrors.script}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('Parameters (Optional)')}</Form.Label>
              <input
                className="input-field"
                type="text"
                value={parameters}
                onChange={(e) => setParameters(e.target.value)}
                placeholder={t('e.g., --ra 1.2 --dec 3.4 --blah blah')}
                data-disabled={submitting ? 'T' : 'F'}
                autoComplete="off"
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <ToolButton
            key={t('Cancel')}
            name="cancel"
            onClick={handleCancel}
            disabled={submitting}
            tooltip={t('Close job submission modal')}
            text={t('Cancel')}
            type={ToolButtonType.SECONDARY}
          />
          <ToolButton
            key={t('Submit')}
            name="submit"
            onClick={handleSubmit}
            disabled={submitting}
            tooltip={t('Submit compute job')}
            text={submitting ? t('Submitting...') : t('Submit Job')}
            type={ToolButtonType.SECONDARY}
          />
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default JobSubmissionForm;
